/* eslint-disable no-param-reassign */
import type { GenericSize } from "../model/geom.ts";
import type { PictureConfiguration } from "../model/picture.ts";
import type { FaceBoundingBox } from "../model/project.ts";
import { unionFaces } from "../model/project.ts";
import { hslToRgb } from "../utils/colour-conversions.ts";
import getPictureSourceImageBounds from "./get-picture-source-image-bounds.ts";

function renderDebugFaces(
	context2d: CanvasFillStrokeStyles &
		CanvasRect &
		CanvasPath &
		CanvasDrawPath &
		CanvasPathDrawingStyles &
		CanvasState &
		CanvasTransform & {
			readonly canvas: GenericSize<number>;
		},
	sourceSize: GenericSize<number>,
	picture: Pick<
		PictureConfiguration,
		"imageZoom" | "imageZoomOffset" | "numberOfBasePlates" | "basePlateSize"
	>,
	faces: readonly Pick<FaceBoundingBox, "x" | "y" | "width" | "height">[],
) {
	if (faces.length === 0) {
		return;
	}

	const sourceBounds = getPictureSourceImageBounds(sourceSize, picture);

	context2d.lineWidth = 1;
	// The canvas displays the sourceBounds region of the source image stretched
	// across the whole canvas (see render-source.ts), so project source pixels
	// onto canvas pixels.
	const scaleX = context2d.canvas.width / sourceBounds.width;
	const scaleY = context2d.canvas.height / sourceBounds.height;

	function drawFace(
		face: Pick<FaceBoundingBox, "x" | "y" | "width" | "height">,
		r: number,
		g: number,
		b: number,
	) {
		context2d.strokeStyle = `rgba(${r}, ${g}, ${b}, 1)`;
		context2d.fillStyle = `rgba(${r}, ${g}, ${b}, 0.5)`;
		context2d.beginPath();
		context2d.rect(
			(face.x * sourceSize.width - sourceBounds.x) * scaleX,
			(face.y * sourceSize.height - sourceBounds.y) * scaleY,
			face.width * sourceSize.width * scaleX,
			face.height * sourceSize.height * scaleY,
		);
		context2d.closePath();
		context2d.stroke();
		context2d.fill();
	}

	drawFace(unionFaces(faces), 0xff, 0xff, 0xff);
	for (let i = 0; i < faces.length; i++) {
		const face = faces[i];
		const hue = (i * Math.floor(360 / (faces.length + 1))) % 360;
		const { r, g, b } = hslToRgb(hue, 0.75, 0.65);
		drawFace(face, r, g, b);
	}
}

export default renderDebugFaces;
